import { useState, useCallback } from 'react';
import { Schedule } from '../types';

export function useSchedules() {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedScheduleRepo, setSelectedScheduleRepo] = useState('');

  const loadSchedules = useCallback(async (owner: string, repo: string) => {
    try {
      const response = await fetch(`/api/v1/schedules/repos/${owner}/${repo}`, {
        credentials: 'same-origin',
      });

      const data = await response.json();
      setSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading schedules:', error);
      setSchedules([]);
    }
  }, []);

  const createSchedule = useCallback(async (scheduleData: Partial<Schedule>) => {
    try {
      const response = await fetch('/api/v1/schedules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(scheduleData),
      });

      const data = await response.json();
      return { success: response.ok, data, error: data.error };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  }, []);

  const updateSchedule = useCallback(
    async (scheduleId: string, scheduleData: Partial<Schedule>) => {
      try {
        const response = await fetch(`/api/v1/schedules/${scheduleId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(scheduleData),
        });

        const data = await response.json();
        return { success: response.ok, data, error: data.error };
      } catch (error) {
        return { success: false, error: (error as Error).message };
      }
    },
    []
  );

  const deleteSchedule = useCallback(async (scheduleId: string) => {
    try {
      const response = await fetch(`/api/v1/schedules/${scheduleId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });

      const data = await response.json();
      return { success: data.success, error: data.error };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  }, []);

  const toggleSchedule = useCallback(
    async (scheduleId: string, currentState: boolean) => {
      return updateSchedule(scheduleId, { enabled: !currentState });
    },
    [updateSchedule]
  );

  return {
    schedules,
    selectedScheduleRepo,
    setSelectedScheduleRepo,
    loadSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    toggleSchedule,
  };
}
